/**
 * Base URL of the public SOS page (the separate `travelkit/` deploy), for the
 * links the adviser tool hands to clients.
 *
 * It used to be hardcoded to `https://sos.portfoplus.com`, so a beta build of
 * this app produced links to the production SOS page — testing on beta opened
 * production.
 *
 * Resolution, first match wins:
 * 1. `PFPLUS_TRAVELKIT_URL`, when set — the explicit override. The old name
 *    `PFPLUS_SOS_URL` is still read as a fallback, so a build host that sets it
 *    keeps working.
 * 2. Derived from `PFPLUS_URL`, which every build already sets:
 *    `app.portfoplus.com` → `sos.portfoplus.com`,
 *    `beta-app.portfoplus.com` → `beta-sos.portfoplus.com`,
 *    `localhost:3000` → `localhost:3021` (the travelkit dev server).
 *    So the existing CodeBuild projects need no new variable.
 * 3. Production, when neither says anything usable. A link that opens the real
 *    page is a better failure than one that opens nothing.
 */

export const TRAVELKIT_PRODUCTION_URL = 'https://sos.portfoplus.com';

/** travelkit's `astro dev` port — see `.claude/launch.json`. */
const TRAVELKIT_DEV_PORT = '3021';

type EnvLike = Record<string, string | undefined>;

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');
const unquote = (value: string): string => value.trim().replace(/^["']|["']$/g, '');

export function resolveTravelKitUrl(env: EnvLike): string {
  const explicit = unquote(env.PFPLUS_TRAVELKIT_URL || env.PFPLUS_SOS_URL || '');
  if (explicit) return stripTrailingSlash(explicit);

  const appUrl = unquote(env.PFPLUS_URL || '');
  if (appUrl) {
    try {
      const url = new URL(appUrl);
      if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
        return `${url.protocol}//${url.hostname}:${TRAVELKIT_DEV_PORT}`;
      }
      // `app.` / `beta-app.` → `sos.` / `beta-sos.`, anchored to the first
      // label so an unrelated host is left alone rather than half-rewritten.
      const travelKitHost = url.hostname.replace(/^((?:[a-z0-9]+-)?)app\./i, '$1sos.');
      if (travelKitHost !== url.hostname) {
        return `${url.protocol}//${travelKitHost}`;
      }
    } catch {
      // Not a URL — fall through to production.
    }
  }

  return TRAVELKIT_PRODUCTION_URL;
}

/** For app code. Webpack's DefinePlugin inlines `process.env` at build time. */
export function getTravelKitUrl(): string {
  return resolveTravelKitUrl({
    PFPLUS_TRAVELKIT_URL: process.env.PFPLUS_TRAVELKIT_URL,
    PFPLUS_SOS_URL: process.env.PFPLUS_SOS_URL,
    PFPLUS_URL: process.env.PFPLUS_URL,
  });
}
